/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDoSeeDataContext } from "../../../context/doSeeData/DoSeeDataContext";
import { pushOneParty } from "../../../middlewares/sysNavigation/SysNavigation";
import { BreadCrumbComponent } from "../../../middlewares/breadCrumb/BreadCrumbComponent";
import {
  Modal,
  GoBack,
  MyButton,
  MySearch,
  Pagination,
  DashboardIntro,
} from "../../home/component/Component";
import { _TYPES_ } from "../utils/types";
import { _PAGE_SIZE_ } from "../../../middlewares/other/tools";
import { useGeoPositionContext } from "../utils/geoPosition";
import { useAdminContext } from "../../../context/admin/context/AdminContext";
import PromoteContent from "../utils/PromoteContent";
import {
  FaRegCheckSquare,
  FaRegSquare,
  FaRegTrashAlt,
  FaRegMinusSquare,
} from "react-icons/fa";
import { RiRefreshLine } from "react-icons/ri";

const addRoute = "/data/party/add";
const editRoute = "/data/party/edit";
const doseeDataRoutes = "/dosee/data";

const Overview = () => {
  const { parties, partiesCount, fetchDoSeeData, deleteOneContent } =
    useDoSeeDataContext();

  const [partiesList, setPartiesList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(_PAGE_SIZE_);

  const [localSearch, setLocalSearch] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedContentIds, setSelectedContentIds] = useState([]);

  const {
    country_id,
    entry_label,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const data_request_body = {
    country_id,
    first_level_id,
    second_level_id,
    third_level_id,
    fourth_level_id,
    content: _TYPES_.PARTY,
  };

  // ---- Design tokens (consistent across pages) ----
  const container = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
  const card = "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const sectionPad = "p-4 sm:p-5";
  const btnBase =
    "inline-flex items-center gap-2 px-3.5 py-2.5 rounded-lg border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const btnPrimary =
    "bg-blue-600 text-white border-blue-600 hover:bg-blue-500 focus-visible:ring-blue-500";
  const btnNeutral =
    "bg-white text-slate-700 border-slate-200 hover:bg-slate-50";
  const btnDanger =
    "bg-red-600 text-white border-red-600 hover:bg-red-500 focus-visible:ring-red-500";

  useEffect(() => {
    if (parties == null) fetchDoSeeData(data_request_body, page, pageSize);
    else setPartiesList(parties);
  }, [parties]);

  const reloadData = () => fetchDoSeeData(data_request_body, page, pageSize);

  // Local (client-side) filtering
  const SearchList = (e) => {
    const key = e.target.value?.toString().toLowerCase() || "";
    if (!key) {
      setPartiesList(parties || []);
      return;
    }
    const searchArray = [];
    parties?.forEach((pty) => {
      if (pty?.title?.toString().toLowerCase().includes(key)) {
        searchArray.push(pty);
      }
    });
    setPartiesList(searchArray);
  };

  const LocalData = localSearch?.length > 0 ? localSearch : partiesList;

  // --- Selection helpers (robust "Select All" on visible rows) ---
  const visibleIds = useMemo(
    () => new Set((LocalData || []).map((p) => p?.party_id)),
    [LocalData]
  );
  const selectedVisibleCount = useMemo(
    () => selectedContentIds.filter((id) => visibleIds.has(id)).length,
    [selectedContentIds, visibleIds]
  );
  const allVisibleSelected =
    (LocalData?.length || 0) > 0 &&
    selectedVisibleCount === (LocalData?.length || 0);
  const someVisibleSelected =
    selectedVisibleCount > 0 && !allVisibleSelected;

  const toggleSelectAllVisible = () => {
    if (!LocalData || LocalData.length === 0) return;

    if (allVisibleSelected) {
      // Unselect all visible (keep selections from other pages)
      setSelectedContentIds((prev) => prev.filter((id) => !visibleIds.has(id)));
    } else {
      // Add all visible (keep existing)
      const toAdd = LocalData.map((p) => p?.party_id).filter(
        (id) => !selectedContentIds.includes(id)
      );
      setSelectedContentIds((prev) => [...prev, ...toAdd]);
    }
  };

  const toggleOne = (id) => {
    setSelectedContentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Clean up selections when dataset changes (remove IDs that no longer exist anywhere)
  useEffect(() => {
    if (!parties || parties.length === 0) {
      setSelectedContentIds([]);
      return;
    }
    const globalIds = new Set(parties.map((p) => p?.party_id));
    setSelectedContentIds((prev) => prev.filter((id) => globalIds.has(id)));
  }, [parties]);

  const deleteMany = async () => {
    if (selectedContentIds.length === 0) return;
    setLoading(true);
    try {
      await Promise.all(
        selectedContentIds.map((id) =>
          deleteOneContent({ data_id: id, content: _TYPES_.PARTY })
        )
      );
      setSelectedContentIds([]);
      reloadData();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className={`${container} py-4 lg:py-6`}>
        {/* Header */}
        <div className="mb-3">
          <BreadCrumbComponent />
        </div>
        <div className="flex items-center justify-between mb-2">
          <GoBack props={{ route: doseeDataRoutes }} />
        </div>
        <div className="mb-4">
          <DashboardIntro props={{ intro: `Parties`, name: entry_label }} />
        </div>

        {/* Search + Add */}
        <div className={`${card} ${sectionPad} mb-4`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <MySearch
              type={_TYPES_.PARTY}
              value={localSearch}
              showGlobalSearch={true}
              setValue={setLocalSearch}
              onChange={SearchList}
            />
            {country_id ? (
              <Link to={addRoute} className="shrink-0">
                <MyButton
                  title="Add Party"
                  className={`${btnBase} ${btnPrimary}`}
                  onClick={() => { }}
                />
              </Link>
            ) : null}
          </div>
        </div>

        {/* Bulk actions */}
        <div className={`${card} ${sectionPad} mb-4`}>
          <div className="flex flex-wrap items-center gap-3">
            <MyButton
              className={`${btnBase} ${btnNeutral}`}
              onClick={toggleSelectAllVisible}
              aria-pressed={allVisibleSelected || someVisibleSelected}
            >
              {allVisibleSelected ? (
                <FaRegCheckSquare className="text-green-600 text-lg" />
              ) : someVisibleSelected ? (
                <FaRegMinusSquare className="text-amber-600 text-lg" />
              ) : (
                <FaRegSquare className="text-slate-700 text-lg" />
              )}
              <span className="text-slate-700">
                {allVisibleSelected ? "Unselect All" : "Select Visible"}
              </span>
              {selectedVisibleCount > 0 && (
                <span className="text-slate-500">
                  ({selectedVisibleCount}/{LocalData?.length || 0})
                </span>
              )}
            </MyButton>

            <MyButton
              onClick={deleteMany}
              className={`${btnBase} ${selectedContentIds.length > 0 ? btnDanger : btnNeutral
                }`}
              disabled={selectedContentIds.length === 0 || loading}
            >
              {loading ? (
                <RiRefreshLine className="text-lg animate-spin" />
              ) : (
                <FaRegTrashAlt className="text-base" />
              )}
              <span>
                Delete{" "}
                {selectedContentIds.length > 0 ? selectedContentIds.length : ""}{" "}
                {selectedContentIds.length === 1 ? "Item" : "Items"}
              </span>
            </MyButton>
          </div>
        </div>

        {/* Table */}
        <div className={`${card} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold w-[120px]">
                    Select
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">Title</th>
                  <th className="px-4 py-3 text-left font-semibold">Slug</th>
                  <th className="px-4 py-3 text-center font-semibold w-[220px]">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {LocalData && LocalData.length > 0 ? (
                  LocalData.map((pty, idx) => {
                    const id = pty?.party_id;
                    const isSelected = selectedContentIds.includes(id);
                    return (
                      <tr
                        key={id ?? idx}
                        className="hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <MyButton
                            className={`${btnBase} ${btnNeutral}`}
                            onClick={() => toggleOne(id)}
                          >
                            {isSelected ? (
                              <FaRegCheckSquare className="text-green-600 text-lg" />
                            ) : (
                              <FaRegSquare className="text-slate-700 text-lg" />
                            )}
                          </MyButton>
                        </td>
                        <td className="px-4 py-3">{pty?.title}</td>
                        <td className="px-4 py-3">{pty?.slug}</td>
                        <td className="px-2 py-3">
                          <ActionButton props={pty} reloadData={reloadData} />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr className="animate-pulse">
                    <td className="px-4 py-3">...</td>
                    <td className="px-4 py-3">...</td>
                    <td className="px-4 py-3">...</td>
                    <td className="px-4 py-3">...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className={`${card} ${sectionPad} mt-4`}>
          <Pagination
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            total_items={partiesCount}
            callbackFunction={(p, size) => {
              fetchDoSeeData(data_request_body, p, size);
            }}
          />
        </div>
      </div>
    </div>
  );
};

/**
 *  @author Ntavigwa Bashombe
 * @param {*} props
 * @returns
 */
const ActionButton = ({ props, reloadData }) => {
  const { sysAdmin } = useAdminContext();
  const isSuperAdmin = ["super_admin", "admin"].includes(sysAdmin?.role);
  const { parties, deleteOneContent, updateOneContent } = useDoSeeDataContext();

  const [showModal, setShowModal] = useState(false);
  const [promote, setPromote] = useState(false);
  const [promoteLoader, setPromoteLoader] = useState(false);

  const party_id = props.party_id;
  const party_title = props.title;
  const party = props;

  const deleteOnePartyById = async (id) => {
    const { status } = await deleteOneContent({
      data_id: id,
      content: _TYPES_.PARTY,
    });
    if (status === 200) reloadData();
  };

  const buildData = (object, position) => ({
    promoted: `${position}`,
    country_id: object?.country_id,
    description: object?.description,
    first_level_id: object?.first_level_id,
    fourth_level_id: object?.fourth_level_id,
    second_level_id: object?.second_level_id,
    slug: object?.slug,
    status: object?.status,
    third_level_id: object?.third_level_id,
    thumbnail: object?.thumbnail,
    title: object?.title,
  });

  const promotion = async (position) => {
    const previousPromoted = parties?.find(
      (p) => p?.promoted === String(position)
    );

    setPromoteLoader(true);

    const { status } = await updateOneContent({
      data_id: party?.party_id,
      data: buildData(props, position),
      content: _TYPES_.PARTY,
    });

    if (previousPromoted) {
      await updateOneContent({
        data_id: previousPromoted?.party_id,
        data: buildData(previousPromoted, "no"),
        content: _TYPES_.PARTY,
      });
    }

    if (status === 200) reloadData();
    setPromoteLoader(false);
  };

  const suspension = async (p) => {
    const data = {
      country_id: p?.country_id,
      description: p?.description,
      first_level_id: p?.first_level_id,
      fourth_level_id: p?.fourth_level_id,
      second_level_id: p?.second_level_id,
      slug: p?.slug,
      status: p?.status === 1 ? 0 : 1,
      third_level_id: p?.third_level_id,
      thumbnail: p?.thumbnail,
      title: p?.title,
    };

    const { status } = await updateOneContent({
      data_id: p?.party_id,
      data,
      content: _TYPES_.PARTY,
    });

    if (status === 200) reloadData();
  };

  // Buttons look & feel
  const btnBase =
    "inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 text-xs";
  const btnNeutral =
    "bg-white text-slate-700 border-slate-200 hover:bg-slate-50";
  const btnInfo =
    "bg-blue-700 text-white border-blue-700 hover:bg-blue-600 focus-visible:ring-blue-600";
  const btnWarn =
    "bg-amber-600 text-white border-amber-600 hover:bg-amber-500 focus-visible:ring-amber-500";
  const btnDanger =
    "bg-red-600 text-white border-red-600 hover:bg-red-500 focus-visible:ring-red-500";
  const btnPrimary =
    "bg-blue-600 text-white border-blue-600 hover:bg-blue-500 focus-visible:ring-blue-500";

  return (
    <>
      <Modal
        props={{
          name: party_title,
          showModal: showModal,
          setShowModal: () => setShowModal(false),
          delete: () => deleteOnePartyById(party_id),
        }}
      />

      <PromoteContent
        visible={promote}
        setVisible={setPromote}
        loading={promoteLoader}
        ids={parties
          ?.filter(
            (p) => !["yes", "no", null, undefined].includes(p?.promoted)
          )
          .map((p) => ({
            id: p?.party_id,
            title: p?.title,
            position: parseInt(p?.promoted),
          }))}
        onClick={(position) => promotion(position)}
      />

      <div className="flex flex-row justify-end items-center px-2 py-2 gap-2">
        {isSuperAdmin ? (
          <>
            <MyButton
              title={party?.status === 1 ? "Suspend" : "Unsuspend"}
              className={`${btnBase} ${party?.status === 1 ? btnPrimary : btnWarn
                }`}
              onClick={() => suspension(party)}
            />
            <MyButton
              title={
                ["yes", "no", null, undefined].includes(party?.promoted)
                  ? "Promote"
                  : "Unpromote"
              }
              className={`${btnBase} ${["yes", "no", null, undefined].includes(party?.promoted)
                ? btnNeutral
                : btnWarn
                }`}
              onClick={() => setPromote(true)}
            />
          </>
        ) : null}

        <Link to={editRoute}>
          <MyButton
            title="Edit"
            className={`${btnBase} ${btnInfo}`}
            onClick={() => pushOneParty(party)}
          />
        </Link>

        <MyButton
          title="Delete"
          className={`${btnBase} ${btnDanger}`}
          onClick={() => setShowModal(true)}
        />
      </div>
    </>
  );
};

export default Overview;
